import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * 🆕 Feb 2026 — Select unificato per metodi di pagamento offline.
 * Include tutti i metodi standard usati in un'agenzia assicurativa + opzione "Altro"
 * con input testo libero per metodi personalizzati (es. "Ricevuta cassa", "Postepay ricaricabile").
 *
 * value semantico:
 *  - una delle chiavi standard: bonifico, contanti, pos, assegno, assegno_circolare, sdd, bollettino, altro
 *  - se "altro", customLabel viene concatenato: "altro:<customLabel>" (spesso salvato tal quale in DB)
 *
 * Props:
 *  - value: string
 *  - onChange: (newValue: string) => void
 *  - testid?: string per il SelectTrigger
 */
export const PAYMENT_METHOD_OPTIONS = [
  { value: "bonifico", label: "Bonifico bancario" },
  { value: "contanti", label: "Contanti in agenzia" },
  { value: "pos", label: "POS / Carta in agenzia" },
  { value: "assegno", label: "Assegno bancario" },
  { value: "assegno_circolare", label: "Assegno circolare" },
  { value: "sdd", label: "Addebito SDD (SEPA)" },
  { value: "bollettino", label: "Bollettino postale" },
  { value: "altro", label: "Altro (specifica)…" },
];

const PaymentMethodSelect = ({ value, onChange, testid = "payment-method" }) => {
  // Se value contiene "altro:<qualcosa>", lo scompongo
  const isCustom = typeof value === "string" && value.startsWith("altro:");
  const selectedKey = isCustom ? "altro" : (value || "");
  const customLabel = isCustom ? value.slice("altro:".length) : "";

  const handleSelectChange = (v) => {
    if (v === "altro") {
      // Se già custom, mantieni label; altrimenti apri il campo vuoto
      onChange(customLabel ? `altro:${customLabel}` : "altro:");
    } else {
      onChange(v);
    }
  };

  const handleCustomLabelChange = (e) => {
    const s = (e.target.value || "").trim();
    onChange(s ? `altro:${s}` : "altro:");
  };

  return (
    <div className="space-y-2">
      <Select value={selectedKey} onValueChange={handleSelectChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder="Seleziona metodo…" /></SelectTrigger>
        <SelectContent>
          {PAYMENT_METHOD_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {selectedKey === "altro" && (
        <div>
          <Label className="text-[11px] text-slate-600">Descrivi il metodo</Label>
          <Input
            data-testid={`${testid}-custom`}
            value={customLabel}
            onChange={handleCustomLabelChange}
            placeholder="Es. Ricevuta cassa, Postepay, Vaglia postale…"
            className="mt-0.5"
          />
        </div>
      )}
    </div>
  );
};

export default PaymentMethodSelect;
